import { useState, useRef, useEffect } from 'react';
import { Mic, Square, X, Loader2, AlertCircle, Sparkles, Activity, Volume2, Zap, Heart } from 'lucide-react';
import { analyzeVoice } from '../lib/mock';

/**
 * Voice Recorder Modal
 * mode: 'chat' — short voice message, no timer limit but suggested ~2min
 *       'session' — extended listening session with pre-selected duration (min)
 */
export default function VoiceRecorder({ open, mode = 'chat', patient, sessionMinutes = 5, onClose, onResult }) {
  const [status, setStatus] = useState('idle'); // idle | recording | analyzing | done | error
  const [elapsed, setElapsed] = useState(0);
  const [level, setLevel] = useState(0); // 0..1 mic input level
  const [errorMsg, setErrorMsg] = useState('');
  const [analysis, setAnalysis] = useState(null);
  const [transcript, setTranscript] = useState('');
  const [permissionDenied, setPermissionDenied] = useState(false);

  const mediaRecorderRef = useRef(null);
  const chunksRef = useRef([]);
  const streamRef = useRef(null);
  const audioCtxRef = useRef(null);
  const analyserRef = useRef(null);
  const rafRef = useRef(null);
  const timerRef = useRef(null);

  const maxSeconds = mode === 'session' ? sessionMinutes * 60 : 180;

  useEffect(() => {
    if (!open) reset();
    return () => cleanup();
  }, [open]);

  const cleanup = () => {
    if (timerRef.current) { clearInterval(timerRef.current); timerRef.current = null; }
    if (rafRef.current) { cancelAnimationFrame(rafRef.current); rafRef.current = null; }
    if (mediaRecorderRef.current && mediaRecorderRef.current.state !== 'inactive') {
      try { mediaRecorderRef.current.stop(); } catch (e) { /* ignore */ }
    }
    if (streamRef.current) {
      streamRef.current.getTracks().forEach(t => t.stop());
      streamRef.current = null;
    }
    if (audioCtxRef.current) {
      try { audioCtxRef.current.close(); } catch (e) { /* ignore */ }
      audioCtxRef.current = null;
    }
  };

  const reset = () => {
    cleanup();
    setStatus('idle');
    setElapsed(0);
    setLevel(0);
    setErrorMsg('');
    setAnalysis(null);
    setTranscript('');
    setPermissionDenied(false);
    chunksRef.current = [];
  };

  const startLevelMeter = (stream) => {
    try {
      const ctx = new (window.AudioContext || window.webkitAudioContext)();
      const src = ctx.createMediaStreamSource(stream);
      const analyser = ctx.createAnalyser();
      analyser.fftSize = 256;
      src.connect(analyser);
      audioCtxRef.current = ctx;
      analyserRef.current = analyser;
      const data = new Uint8Array(analyser.frequencyBinCount);
      const tick = () => {
        analyser.getByteFrequencyData(data);
        let sum = 0;
        for (let i = 0; i < data.length; i++) sum += data[i];
        setLevel(Math.min(1, sum / data.length / 128));
        rafRef.current = requestAnimationFrame(tick);
      };
      tick();
    } catch (e) { /* level meter is optional */ }
  };

  const startRecording = async () => {
    setErrorMsg('');
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: { echoCancellation: true, noiseSuppression: true } });
      streamRef.current = stream;

      // Pick a mime supported by browser
      let mimeType = 'audio/webm;codecs=opus';
      if (!window.MediaRecorder || !MediaRecorder.isTypeSupported(mimeType)) {
        mimeType = MediaRecorder.isTypeSupported('audio/webm') ? 'audio/webm'
          : MediaRecorder.isTypeSupported('audio/mp4') ? 'audio/mp4'
          : '';
      }
      const rec = mimeType ? new MediaRecorder(stream, { mimeType }) : new MediaRecorder(stream);
      mediaRecorderRef.current = rec;
      chunksRef.current = [];

      rec.ondataavailable = (e) => { if (e.data && e.data.size > 0) chunksRef.current.push(e.data); };
      rec.onstop = handleStop;
      rec.start(1000);
      setStatus('recording');
      startLevelMeter(stream);

      timerRef.current = setInterval(() => {
        setElapsed(prev => {
          const next = prev + 1;
          if (next >= maxSeconds) { stopRecording(); }
          return next;
        });
      }, 1000);
    } catch (e) {
      console.error(e);
      if (e && (e.name === 'NotAllowedError' || e.name === 'PermissionDeniedError')) {
        setPermissionDenied(true);
      }
      setErrorMsg('تعذّر الوصول إلى المايكروفون. اسمحي للتطبيق باستخدام المايك من إعدادات المتصفح.');
      setStatus('error');
    }
  };

  const stopRecording = () => {
    if (timerRef.current) { clearInterval(timerRef.current); timerRef.current = null; }
    if (rafRef.current) { cancelAnimationFrame(rafRef.current); rafRef.current = null; }
    if (mediaRecorderRef.current && mediaRecorderRef.current.state !== 'inactive') {
      mediaRecorderRef.current.stop();
    }
  };

  const handleStop = async () => {
    const blob = new Blob(chunksRef.current, { type: mediaRecorderRef.current?.mimeType || 'audio/webm' });
    if (streamRef.current) { streamRef.current.getTracks().forEach(t => t.stop()); streamRef.current = null; }
    if (audioCtxRef.current) { try { audioCtxRef.current.close(); } catch (e) { /* ignore */ } audioCtxRef.current = null; }

    if (blob.size < 1500) {
      setErrorMsg('التسجيل قصير جداً — حاولي مرة أخرى.');
      setStatus('error');
      return;
    }

    setStatus('analyzing');
    try {
      const ext = (mediaRecorderRef.current?.mimeType || 'audio/webm').includes('mp4') ? 'mp4' : 'webm';
      const res = await analyzeVoice({
        patientId: patient.id,
        blob,
        mode,
        saveAsSession: mode === 'session',
        filename: `voice.${ext}`,
      });
      setTranscript(res.transcript || '');
      setAnalysis(res.analysis || null);
      setStatus('done');
      if (onResult) onResult(res);
    } catch (e) {
      console.error(e);
      setErrorMsg(e.message || 'تعذّر تحليل التسجيل');
      setStatus('error');
    }
  };

  const fmtTime = (s) => `${String(Math.floor(s / 60)).padStart(2, '0')}:${String(s % 60).padStart(2, '0')}`;

  if (!open) return null;

  const isSession = mode === 'session';

  return (
    <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-black/50 backdrop-blur-sm animate-fade-in" data-testid="voice-recorder-modal">
      <div className="w-full sm:max-w-md bg-white rounded-t-3xl sm:rounded-3xl overflow-hidden shadow-2xl animate-slide-up">
        {/* Header */}
        <div className="relative px-5 py-4" style={{ background: isSession ? 'linear-gradient(135deg, #5eb2a3, #3f8579)' : 'linear-gradient(135deg, #8b7ff0, #6a5cd8)' }}>
          <button onClick={onClose} className="absolute top-3 left-3 w-8 h-8 rounded-full bg-white/20 hover:bg-white/30 flex items-center justify-center backdrop-blur" data-testid="voice-close-btn">
            <X className="w-4 h-4 text-white" />
          </button>
          <div className="text-center">
            <div className="text-white/90 text-[11px] font-bold uppercase tracking-wider mb-1">
              {isSession ? 'جلسة استماع' : 'رسالة صوتية'}
            </div>
            <div className="text-white text-[16px] font-extrabold">
              {isSession ? `${sessionMinutes} دقائق مع الذكاء` : 'تحدثي بحرية'}
            </div>
          </div>
        </div>

        <div className="px-5 py-6">
          {status === 'idle' && (
            <div className="text-center">
              <div className="mb-4 text-[12.5px] leading-relaxed" style={{ color: 'var(--text-2)' }}>
                {isSession
                  ? 'اضغطي "ابدئي" ثم تحدثي أو دعي المايك يسمع من حولك. سيحلل الذكاء كلامك ونبرتك ويقترح خطوات.'
                  : 'اضغطي على الميكروفون وتحدثي عمّا تشعرين به. سنحوّل صوتك إلى نص عربي ونحلل حالتك النفسية.'}
              </div>
              <button
                onClick={startRecording}
                className="mx-auto w-24 h-24 rounded-full flex items-center justify-center shadow-lg active:scale-95 transition-transform"
                style={{ background: isSession ? 'linear-gradient(135deg, #5eb2a3, #3f8579)' : 'linear-gradient(135deg, #8b7ff0, #6a5cd8)' }}
                data-testid="voice-start-btn"
              >
                <Mic className="w-10 h-10 text-white" />
              </button>
              <div className="mt-3 text-[10.5px]" style={{ color: 'var(--text-3)' }}>
                خصوصية تامة • يُحلَّل الصوت ثم يُحذف
              </div>
            </div>
          )}

          {status === 'recording' && (
            <div className="text-center" data-testid="voice-recording-state">
              <div className="relative mx-auto w-32 h-32 flex items-center justify-center mb-4">
                {/* Pulse waves based on mic level */}
                <div className="absolute inset-0 rounded-full animate-ping" style={{ background: isSession ? 'rgba(94,178,163,0.25)' : 'rgba(139,127,240,0.25)', opacity: 0.4 + level * 0.6 }}></div>
                <div className="absolute rounded-full transition-transform" style={{ inset: `${12 - level * 8}px`, background: isSession ? 'rgba(94,178,163,0.35)' : 'rgba(139,127,240,0.35)' }}></div>
                <button onClick={stopRecording} className="relative w-20 h-20 rounded-full flex items-center justify-center shadow-lg" style={{ background: 'linear-gradient(135deg, #e58585, #c46464)' }} data-testid="voice-stop-btn">
                  <Square className="w-7 h-7 text-white" fill="white" />
                </button>
              </div>
              <div className="text-[26px] font-black mb-1" style={{ color: 'var(--text)' }}>{fmtTime(elapsed)}</div>
              <div className="text-[11px]" style={{ color: 'var(--text-2)' }}>
                الحد الأقصى {fmtTime(maxSeconds)} — اضغطي المربّع للإيقاف والتحليل
              </div>
              {/* Level meter bar */}
              <div className="mt-4 h-1.5 rounded-full overflow-hidden mx-auto max-w-[220px]" style={{ background: 'var(--border-soft)' }}>
                <div className="h-full transition-all duration-100 rounded-full" style={{ width: `${Math.min(100, level * 130)}%`, background: isSession ? 'linear-gradient(90deg, #5eb2a3, #7dcfa9)' : 'linear-gradient(90deg, #8b7ff0, #b8afff)' }}></div>
              </div>
            </div>
          )}

          {status === 'analyzing' && (
            <div className="py-8 text-center" data-testid="voice-analyzing-state">
              <Loader2 className="w-10 h-10 mx-auto animate-spin mb-3" style={{ color: isSession ? 'var(--secondary)' : 'var(--primary)' }} />
              <div className="text-[13.5px] font-extrabold mb-1" style={{ color: 'var(--text)' }}>الذكاء يستمع إليكِ…</div>
              <div className="text-[11px]" style={{ color: 'var(--text-2)' }}>ينسخ الصوت ويحلل نبرتك وحالتك النفسية</div>
            </div>
          )}

          {status === 'done' && analysis && (
            <div className="space-y-3 max-h-[60vh] overflow-y-auto" data-testid="voice-result-state">
              {/* Gentle message */}
              <div className="rounded-2xl p-3.5" style={{ background: 'linear-gradient(135deg, #fef3e5, #fef0f0)', border: '1px solid var(--peach)' }}>
                <div className="flex items-center gap-2 mb-1.5">
                  <Heart className="w-4 h-4" style={{ color: '#d17c53' }} fill="#f5b892" />
                  <span className="text-[11px] font-extrabold" style={{ color: '#8a5f3a' }}>رسالة لكِ</span>
                </div>
                <div className="text-[13px] leading-relaxed" style={{ color: 'var(--text)' }}>{analysis.gentle_message}</div>
              </div>

              {/* Transcript */}
              {transcript && (
                <div className="card-soft p-3">
                  <div className="text-[10.5px] font-extrabold mb-1.5" style={{ color: 'var(--text-2)' }}>ما قلتِه</div>
                  <div className="text-[12.5px] leading-relaxed" style={{ color: 'var(--text)' }}>«{transcript}»</div>
                </div>
              )}

              {/* Mood + risk metrics */}
              <div className="grid grid-cols-2 gap-2">
                <MetricCard icon={Activity} label="المزاج" value={analysis.mood_estimate || '—'} color="#8b7ff0" bg="var(--primary-bg)" />
                <MetricCard icon={Zap} label="الطاقة" value={analysis.energy_level || '—'} color="#5eb2a3" bg="var(--secondary-bg)" />
              </div>

              {/* Speech analysis */}
              <div className="rounded-2xl p-3" style={{ background: 'var(--surface-2)', border: '1px solid var(--border-soft)' }}>
                <div className="flex items-center gap-2 mb-1.5">
                  <Volume2 className="w-3.5 h-3.5" style={{ color: 'var(--primary-dark)' }} />
                  <span className="text-[10.5px] font-extrabold" style={{ color: 'var(--text-2)' }}>تحليل الكلام</span>
                </div>
                <div className="text-[12px] leading-relaxed" style={{ color: 'var(--text)' }}>{analysis.speech_analysis || '—'}</div>
                {analysis.emotional_tone && (
                  <div className="mt-2 pt-2 border-t border-[var(--border-soft)]">
                    <div className="text-[10.5px] font-bold mb-1" style={{ color: 'var(--text-2)' }}>النبرة العاطفية</div>
                    <div className="text-[11.5px]" style={{ color: 'var(--text)' }}>{analysis.emotional_tone}</div>
                  </div>
                )}
              </div>

              {/* Key signals */}
              {(analysis.key_signals || []).length > 0 && (
                <div className="card-soft p-3">
                  <div className="flex items-center gap-2 mb-2">
                    <Sparkles className="w-3.5 h-3.5" style={{ color: '#c9871f' }} />
                    <span className="text-[10.5px] font-extrabold" style={{ color: 'var(--text-2)' }}>إشارات ملاحظة</span>
                  </div>
                  <ul className="space-y-1.5">
                    {analysis.key_signals.map((k, i) => (
                      <li key={i} className="flex items-start gap-2 text-[11.5px]" style={{ color: 'var(--text)' }}>
                        <span className="w-1.5 h-1.5 rounded-full mt-1.5 flex-shrink-0" style={{ background: 'var(--primary)' }}></span>
                        <span className="leading-relaxed">{k}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {/* Risk meter */}
              {typeof analysis.risk_score === 'number' && analysis.risk_score > 0 && (
                <div className="rounded-2xl p-3" style={{ background: 'var(--surface)', border: '1px solid var(--border-soft)' }}>
                  <div className="flex items-center justify-between mb-1.5">
                    <span className="text-[10.5px] font-extrabold" style={{ color: 'var(--text-2)' }}>الانتباه السريري</span>
                    <span className="text-[11px] font-extrabold" style={{ color: analysis.risk_score >= 7 ? 'var(--danger)' : analysis.risk_score >= 4 ? 'var(--warning)' : 'var(--success)' }}>
                      {analysis.risk_score}/10
                    </span>
                  </div>
                  <div className="h-1.5 rounded-full overflow-hidden" style={{ background: 'var(--border-soft)' }}>
                    <div className="h-full rounded-full transition-all duration-500" style={{ width: `${analysis.risk_score * 10}%`, background: analysis.risk_score >= 7 ? 'var(--danger)' : analysis.risk_score >= 4 ? 'var(--warning)' : 'var(--success)' }}></div>
                  </div>
                </div>
              )}

              {/* Suggested action */}
              {analysis.suggested_action && analysis.suggested_action !== '—' && (
                <div className="rounded-2xl p-3.5" style={{ background: 'var(--honey-bg)', border: '1px solid var(--honey)' }}>
                  <div className="flex items-center gap-2 mb-1.5">
                    <Sparkles className="w-4 h-4" style={{ color: '#c9871f' }} fill="#f0c674" />
                    <span className="text-[11px] font-extrabold" style={{ color: '#8a5f1a' }}>اقتراح عملي</span>
                  </div>
                  <div className="text-[12.5px] leading-relaxed" style={{ color: '#6b4e18' }}>{analysis.suggested_action}</div>
                </div>
              )}

              <div className="flex gap-2 pt-2">
                <button onClick={reset} className="flex-1 py-3 rounded-2xl text-[12.5px] font-extrabold border-2 transition-colors" style={{ borderColor: 'var(--border)', color: 'var(--text-2)' }} data-testid="voice-record-again-btn">
                  تسجيل جديد
                </button>
                <button onClick={onClose} className="flex-1 py-3 rounded-2xl text-white text-[12.5px] font-extrabold shadow-md" style={{ background: isSession ? 'linear-gradient(135deg, #5eb2a3, #3f8579)' : 'linear-gradient(135deg, #8b7ff0, #6a5cd8)' }} data-testid="voice-done-btn">
                  انتهيت
                </button>
              </div>
            </div>
          )}

          {status === 'error' && (
            <div className="text-center py-6" data-testid="voice-error-state">
              <div className="w-14 h-14 rounded-full mx-auto flex items-center justify-center mb-3" style={{ background: 'var(--danger-bg)' }}>
                <AlertCircle className="w-6 h-6" style={{ color: 'var(--danger)' }} />
              </div>
              <div className="text-[13px] font-extrabold mb-2" style={{ color: 'var(--text)' }}>عذراً، حصل خطأ</div>
              <div className="text-[11.5px] leading-relaxed mb-4 px-3" style={{ color: 'var(--text-2)' }}>{errorMsg}</div>
              {permissionDenied && (
                <div className="text-[10.5px] leading-relaxed mb-3 px-3" style={{ color: 'var(--text-3)' }}>
                  فعّلي إذن الميكروفون من إعدادات المتصفح ثم أعيدي المحاولة.
                </div>
              )}
              <button onClick={reset} className="px-6 py-2.5 rounded-2xl text-white font-extrabold text-[12.5px] shadow-md" style={{ background: 'linear-gradient(135deg, #8b7ff0, #6a5cd8)' }} data-testid="voice-retry-btn">
                إعادة المحاولة
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function MetricCard({ icon: Icon, label, value, color, bg }) {
  return (
    <div className="rounded-2xl p-3" style={{ background: bg, border: `1px solid ${color}33` }}>
      <div className="flex items-center gap-1.5 mb-1">
        <Icon className="w-3.5 h-3.5" style={{ color }} />
        <span className="text-[10px] font-extrabold" style={{ color }}>{label}</span>
      </div>
      <div className="text-[13px] font-extrabold" style={{ color: 'var(--text)' }}>{value}</div>
    </div>
  );
}
